import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useToast } from '@/shared/design-system';

import { entityRolesClient } from './client';
import { getRoleAdminErrorMessage } from './error-codes';
import { ENTITY_ROLES_QUERY_KEYS } from './queries';
import type { RoleScopeKind } from './types';

export const SCOPE_SAVED_TITLE = 'Categories saved.';
export const SCOPE_SAVE_FAILED_TITLE = "Couldn't save the role's categories.";

export type SetEntityRoleScopeInput = {
  roleId: string;
  kind: RoleScopeKind;
  resourceIds: string[];
};

export const useSetEntityRoleScope = ({ entityId }: { entityId?: string | null }) => {
  const queryClient = useQueryClient();
  const { add } = useToast();

  return useMutation({
    mutationFn: ({ roleId, kind, resourceIds }: SetEntityRoleScopeInput) =>
      entityRolesClient.setRoleScope(roleId, kind, { resourceIds }),
    onSuccess: (_result, { roleId }) => {
      add({ title: SCOPE_SAVED_TITLE, data: { status: 'success' } });

      // Settling only after the role refetch keeps the picker locked until the saved answer
      // arrives, so a tick made in between is not lost when the refetch resets the draft.
      return queryClient.invalidateQueries({
        queryKey: [ENTITY_ROLES_QUERY_KEYS.role, entityId, roleId],
      });
    },
    onError: (error) =>
      add({
        title: getRoleAdminErrorMessage(error, SCOPE_SAVE_FAILED_TITLE),
        data: { status: 'error' },
      }),
  });
};
